<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="state" :validate-on="['submit']" class="h-full">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            Add Partner
                        </h3>
                    </div>
                </template>
                <div class="space-y-6">
                    <UFormGroup name="avatar" label="Logo">
                        <AvatarUpload bucket="media" folder="partners" v-model="state.avatar!" :placeholder="state.name" />
                    </UFormGroup>
                    <UFormGroup name="name" label="Name" required>
                        <UInput v-model="state.name" type="text" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="metadata.email_reports" label="Email Reports">
                        <div class="space-y-2.5 mt-2">
                            <div v-for="type in [EmailReportType.callReport, EmailReportType.sickLeave]" :key="type" class="relative border border-gray-200 rounded-md p-4">
                                <div class="flex items-center justify-between">
                                    <p class="text-sm font-medium">{{ emailReportLabel(type) }}</p>
                                    <UToggle size="xs" color="primary" @update:model-value="onToggleEmailReport(type, $event)" :model-value="state.metadata.email_reports?.[type] !== undefined" />
                                </div>
                                <div v-if="state.metadata.email_reports?.[type]" class="space-y-4 pt-6 pb-2">
                                    <UFormGroup
                                        v-if="type === EmailReportType.sickLeave"
                                        name="sick_leave_mode"
                                        label="Route To"
                                    >
                                        <URadioGroup
                                            :model-value="state.metadata.email_reports.sick_leave.mode"
                                            @update:model-value="onSelectSickLeaveMode($event)"
                                            :options="sickLeaveModeOptions"
                                        />
                                    </UFormGroup>

                                    <template v-if="type === EmailReportType.callReport || state.metadata.email_reports.sick_leave.mode === SickLeaveScope.all">
                                        <UFormGroup name="email_to" label="Email To">
                                            <UInput v-model="state.metadata.email_reports[type].email_to" type="text" size="xl" />
                                        </UFormGroup>
                                        <UFormGroup name="email_cc" label="Email CC" description="For multiple emails, separate by commas">
                                            <UInput v-model="state.metadata.email_reports[type].email_cc" type="text" size="xl" />
                                        </UFormGroup>
                                    </template>

                                    <UFormGroup
                                        v-if="type === EmailReportType.callReport"
                                        name="allowed_policy_holders"
                                        label="Allowed Policy Holders"
                                        description="Leave empty to allow all policy holders. When set, only call reports for customers with a matching policy holder will trigger emails."
                                    >
                                        <div class="space-y-2">
                                            <UInputMenu
                                                v-model="selectedPolicyHolder"
                                                :search="searchPolicyHolders"
                                                placeholder="Search..."
                                                nullable
                                            />
                                            <div
                                                v-if="state.metadata.email_reports?.call_report?.allowed_policy_holders?.length"
                                                class="flex flex-wrap gap-1"
                                            >
                                                <UBadge
                                                    v-for="ph in state.metadata.email_reports.call_report.allowed_policy_holders"
                                                    :key="ph"
                                                    size="sm"
                                                    variant="subtle"
                                                    color="gray"
                                                    :ui="{ size: { sm: 'pl-2 pr-0 py-0' } }"
                                                >
                                                    <span class="text-xs truncate">{{ ph }}</span>
                                                    <UButton
                                                        @click="removePolicyHolder(ph)"
                                                        icon="i-ph-x-bold"
                                                        size="2xs"
                                                        color="gray"
                                                        variant="ghost"
                                                    />
                                                </UBadge>
                                            </div>
                                        </div>
                                    </UFormGroup>

                                    <UFormGroup
                                        v-if="type === EmailReportType.sickLeave && state.metadata.email_reports.sick_leave.mode === SickLeaveScope.perPolicyHolder"
                                        name="policy_holders"
                                        label="Policy Holders"
                                        description="Members whose policy holder is not listed here — and members with no policy holder at all — cannot be sent a sick leave."
                                    >
                                        <template #hint>
                                            <UButton @click="onAddSickLeaveRecipient()" icon="i-ph-plus-bold" size="xs" variant="ghost">Add</UButton>
                                        </template>
                                        <div class="space-y-3">
                                            <div
                                                v-for="(entry, i) in state.metadata.email_reports.sick_leave.policy_holders"
                                                :key="i"
                                                class="space-y-2 border border-gray-200 rounded-md p-3"
                                            >
                                                <div class="flex items-start space-x-2">
                                                    <UInputMenu
                                                        :model-value="entry.policy_holder"
                                                        @update:model-value="entry.policy_holder = $event ?? ''"
                                                        :search="searchPolicyHolders"
                                                        placeholder="Search policy holder..."
                                                        nullable
                                                        class="flex-1"
                                                    />
                                                    <UButton
                                                        @click="onRemoveSickLeaveRecipient(i)"
                                                        icon="i-ph-trash-bold"
                                                        size="2xs"
                                                        color="red"
                                                        variant="soft"
                                                        class="mt-1.5"
                                                    />
                                                </div>
                                                <UInput v-model="entry.email_to" type="text" placeholder="Email To" />
                                                <UInput v-model="entry.email_cc" type="text" placeholder="Email CC" />
                                            </div>
                                            <p v-if="!state.metadata.email_reports.sick_leave.policy_holders?.length" class="text-xs text-gray-500">
                                                No policy holders configured. Sick leave cannot be sent for this partner.
                                            </p>
                                        </div>
                                    </UFormGroup>
                                </div>
                            </div>
                        </div>
                    </UFormGroup>
                    <UCheckbox 
                        v-model="state.metadata.is_public" 
                        @change="resetSubscriptionData()"
                        name="is_public" 
                        label="Show on app" 
                        help="When checked, partner will be visible on the app."
                    />
                    <UCheckbox 
                        v-model="state.metadata.can_create_coupons" 
                        name="can_create_coupons" 
                        label="Can create coupons" 
                        help="When checked, partner will be able to generate coupons."
                    />
                    <template v-if="state.metadata.is_public">
                        <UFormGroup 
                            required
                            name="metadata.subscription_instructions" 
                            label="Subscription Instructions" 
                            description="Enter the instructions that users will see when activating a subscription with this partner." 
                        >
                            <UTextarea v-model="state.metadata.subscription_instructions" size="xl" />
                        </UFormGroup>
                        <UFormGroup 
                            required
                            name="metadata.subscription_fields" 
                            label="Subscription Fields" 
                            description="Manage fields that are required for users to activate a subscription. Non-required fields are for internal use."
                        >
                            <template #hint>
                                <UButton @click="onAddSubscriptionField()" icon="i-ph-plus-bold" size="xs" variant="ghost">Add</UButton>
                            </template>
                            <div class="space-y-2.5">
                                <div v-for="(field, i) in state.metadata.subscription_fields" :key="i" class="relative">
                                    <UInput  
                                        v-model="field.label"
                                        @input="field.key = snakeCase($event.target.value)"
                                        type="text"
                                        size="xl"
                                        placeholder="Enter field name..."
                                        :ui="{ padding: { xl: 'ps-3.5 pe-11 pt-2.5 pb-10' } }"
                                    />
                                    <div v-if="state.metadata.subscription_fields.length > 1" class="absolute top-0 bottom-7 right-0 px-3 flex items-center">
                                        <UButton
                                            @click="onRemoveSubscriptionField(i)"
                                            icon="i-ph-trash-bold"
                                            size="2xs"
                                            color="red"
                                            variant="soft"
                                        />
                                    </div>
                                    <div class="absolute bottom-px inset-x-px px-3 h-7 flex items-center justify-between space-x-2 border-t rounded-b bg-gray-100 text-gray-500 border-gray-300">
                                        <div class="flex items-center space-x-1.5 text-[11px]">
                                            <span v-if="field.key" class="mt-px uppercase tracking-wide">Key:</span>
                                            <span class="font-medium">{{ field.key }}</span>
                                        </div>
                                        <div class="flex items-center space-x-2">
                                            <span class="mt-px text-[11px] uppercase tracking-wide">Required:</span>
                                            <UToggle size="xs" color="primary" v-model="field.required" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </UFormGroup>
                    </template>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>
                        <UButton type="submit" size="lg" :loading="loading" color="black">
                            Save
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { snakeCase } from 'lodash'
import type { FormSubmitEvent } from '#ui/types'
import AvatarUpload from '~/components/input/AvatarUpload.vue'
import { EmailReportType, SickLeaveScope } from '~/types/partner';
import { sickLeaveSchema } from '~/utils/sickLeaveSchema'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
}>()

const client = useSupabaseClient()
const toast = useToast()

const form = ref()
const state = ref<z.infer<typeof schema>>({} as z.infer<typeof schema>)
const loading = ref<boolean>(false)
const selectedPolicyHolder = ref<string | undefined>(undefined)

const sickLeaveModeOptions = [
    { value: SickLeaveScope.all, label: 'All members of this partner' },
    { value: SickLeaveScope.perPolicyHolder, label: 'Specific policy holders' },
]

const schema = z.object({
    avatar: z.string().nullable().optional(),
    name: z.string().min(1),
    metadata: z.object({
        is_public: z.boolean(),
        can_create_coupons: z.boolean(),
        subscription_instructions: z.string().optional(),
        email_reports: z.object({
            call_report: z.object({
                email_to: z.string(),
                email_cc: z.string().optional(),
                allowed_policy_holders: z.array(z.string()).optional(),
            }).optional(),
            sick_leave: sickLeaveSchema.optional(),
        }).optional(),
        subscription_fields: z.array(z.object({
            key: z.string().optional(),
            label: z.string().optional(),
            required: z.boolean().optional()
        }))
    }).refine((metadata) => {
        if (metadata.is_public && !metadata.subscription_instructions) {
            return false
        }
        return true
    }, {
        path: ['subscription_instructions']
    }).refine((metadata) => {
        if (metadata.is_public && (!metadata.subscription_fields.length || metadata.subscription_fields.filter(f => !f.label).length)) {
            return false
        }
        return true
    }, {
        path: ['subscription_fields']
    })
})

watch(() => props.open, (open) => {
    if (open) {
        state.value = {
            avatar: '',
            name: '',
            metadata: {
                is_public: true,
                can_create_coupons: true,
                email_reports: undefined,
                subscription_instructions: undefined,
                subscription_fields: [{
                    key: '',
                    label: '',
                    required: true
                }]
            }
        }
    }
})

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    loading.value = true

    const { data, error } = await client
        .from('partners')
        .insert(event.data as never)
        .select()

    if (data && data.length > 0) {
        dismiss(true)
    }
    else if (error) {
        console.error(error)
        toast.add({ title: 'Could not save partner', description: error.message, color: 'red' })
    }

    loading.value = false
}

function emailReportLabel(type: EmailReportType) {
    switch (type) {
        case EmailReportType.callReport: return 'Call Report'
        case EmailReportType.sickLeave: return 'Sick Leave'
    }
}

function onToggleEmailReport(type: EmailReportType, toggle: boolean) {
    if (!state.value.metadata.email_reports) {
        state.value.metadata.email_reports = {}
    }

    if (toggle === false) {
        state.value.metadata.email_reports[type] = undefined
        return
    }

    state.value.metadata.email_reports[type] = type === EmailReportType.sickLeave
        ? { mode: SickLeaveScope.all, email_to: '', email_cc: '' }
        : { email_to: '', email_cc: '' }
}

function onSelectSickLeaveMode(mode: SickLeaveScope) {
    const emailReports = state.value?.metadata?.email_reports
    if (!emailReports?.sick_leave || emailReports.sick_leave.mode === mode) return

    emailReports.sick_leave = mode === SickLeaveScope.all
        ? { mode: SickLeaveScope.all, email_to: '', email_cc: '' }
        : { mode: SickLeaveScope.perPolicyHolder, policy_holders: [] }
}

function onAddSickLeaveRecipient() {
    const sickLeave = state.value?.metadata?.email_reports?.sick_leave
    if (sickLeave?.mode !== SickLeaveScope.perPolicyHolder) return

    if (!sickLeave.policy_holders) sickLeave.policy_holders = []
    sickLeave.policy_holders.push({ policy_holder: '', email_to: '', email_cc: '' })
}

function onRemoveSickLeaveRecipient(index: number) {
    const sickLeave = state.value?.metadata?.email_reports?.sick_leave
    if (sickLeave?.mode !== SickLeaveScope.perPolicyHolder) return

    sickLeave.policy_holders.splice(index, 1)
}

function onAddSubscriptionField() {
    state.value.metadata.subscription_fields.push({
        key: '',
        label: '',
        required: true
    })
}

function onRemoveSubscriptionField(index: number) {
    state.value.metadata.subscription_fields.splice(index, 1)
}

function resetSubscriptionData() {
    state.value.metadata.subscription_instructions = undefined
    state.value.metadata.subscription_fields = []
    onAddSubscriptionField()
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)

    if (reload) {
        emit('reload', true)
    }
}

async function searchPolicyHolders(q: string): Promise<string[]> {
    const { data, error } = await client
        .rpc('get_policy_holder_suggestions', { partial_name: q } as never)
        .returns<{ policy_holder: string }[]>()

    if (error) return []
    return data.map(t => t.policy_holder)
}

function removePolicyHolder(ph: string) {
    const callReport = state.value?.metadata?.email_reports?.call_report
    if (!callReport) return
    const next = (callReport.allowed_policy_holders || []).filter((p: string) => p !== ph)
    callReport.allowed_policy_holders = next.length ? next : undefined
}

watch(selectedPolicyHolder, (ph) => {
    if (!ph) return
    const callReport = state.value?.metadata?.email_reports?.call_report
    if (!callReport) return
    const merged = [...(callReport.allowed_policy_holders || []), ph]
    callReport.allowed_policy_holders = [...new Set(merged)]
    nextTick(() => { selectedPolicyHolder.value = undefined })
})
</script>